import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../../api/supabase'
import type { Permission } from '../../../hooks/usePermissionGroups'

interface GroupData {
  id: string
  name: string
  description: string | null
  is_system: boolean
  is_active: boolean
}

export default function GroupEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isSystem, setIsSystem] = useState(false)
  const [allPermissions, setAllPermissions] = useState<Permission[]>([])
  const [checkedCodes, setCheckedCodes] = useState<Set<string>>(new Set())
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    setLoading(true)

    const { data: perms } = await supabase
      .from('permissions')
      .select('*')
      .eq('is_active', true)
      .order('module')
      .order('description')
    setAllPermissions(perms || [])

    if (!isNew && id) {
      const { data: group } = await supabase
        .from('permission_groups')
        .select('*')
        .eq('id', id)
        .single<GroupData>()

      if (group) {
        setName(group.name)
        setDescription(group.description || '')
        setIsSystem(group.is_system)
      }

      const { data: groupPerms } = await supabase
        .from('group_permissions')
        .select('permission_code')
        .eq('group_id', id)
      setCheckedCodes(new Set(groupPerms?.map((p: { permission_code: string }) => p.permission_code) || []))
    }

    setLoading(false)
  }, [id, isNew])

  useEffect(() => { loadData() }, [loadData])

  const togglePermission = (code: string) => {
    setCheckedCodes(prev => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  const toggleModule = (modulePerms: Permission[]) => {
    const moduleCodes = modulePerms.map(p => p.code)
    const allChecked = moduleCodes.every(c => checkedCodes.has(c))
    setCheckedCodes(prev => {
      const next = new Set(prev)
      if (allChecked) moduleCodes.forEach(c => next.delete(c))
      else moduleCodes.forEach(c => next.add(c))
      return next
    })
  }

  const toggleCollapse = (module: string) => {
    setCollapsed(prev => ({ ...prev, [module]: !prev[module] }))
  }

  const handleSave = async () => {
    if (!name.trim()) { setError('Group name is required'); return }
    setError(null)
    setSaving(true)

    let groupId = isNew ? null : id

    if (isNew) {
      const { data, error: insertErr } = await supabase
        .from('permission_groups')
        .insert({ name: name.trim(), description: description.trim() || null, is_system: false, is_active: true })
        .select('id')
        .single<{ id: string }>()
      if (insertErr) { setError(insertErr.message); setSaving(false); return }
      groupId = data?.id ?? null
    } else {
      const { error: updateErr } = await supabase
        .from('permission_groups')
        .update({ name: name.trim(), description: description.trim() || null })
        .eq('id', id)
      if (updateErr) { setError(updateErr.message); setSaving(false); return }
    }

    if (groupId) {
      await supabase.from('group_permissions').delete().eq('group_id', groupId)
      if (checkedCodes.size > 0) {
        await supabase.from('group_permissions').insert(
          Array.from(checkedCodes).map(code => ({ group_id: groupId, permission_code: code }))
        )
      }
    }

    setSaving(false)
    navigate('/admin/groups')
  }

  // Group permissions by module
  const permsByModule = allPermissions.reduce<Record<string, Permission[]>>((acc, p) => {
    if (!acc[p.module]) acc[p.module] = []
    acc[p.module].push(p)
    return acc
  }, {})

  const moduleOrder = ['Jobs', 'Invoices', 'Quotes', 'Customs', 'Warehouse', 'Data', 'Admin', 'Reports']
  const sortedModules = [
    ...moduleOrder.filter(m => permsByModule[m]),
    ...Object.keys(permsByModule).filter(m => !moduleOrder.includes(m)).sort(),
  ]

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading...</div>
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/admin/groups')}
            className="text-sm text-blue-600 hover:underline mb-2 flex items-center gap-1"
          >
            ← Back to Groups
          </button>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
            {isNew ? 'Create New Group' : `Edit Group`}
          </h1>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !name.trim()}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 mt-6"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Group info */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Group Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={isSystem}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              placeholder="e.g. CUSTOMS_TEAM"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Short description"
            />
          </div>
        </div>
        {isSystem && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
            System group — name cannot be changed
          </p>
        )}
      </div>

      {/* Permissions by module */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          Permissions
        </h2>
        {sortedModules.map(moduleName => {
          const modulePerms = permsByModule[moduleName]
          const moduleCodes = modulePerms.map(p => p.code)
          const checkedCount = moduleCodes.filter(c => checkedCodes.has(c)).length
          const allChecked = checkedCount === moduleCodes.length
          const someChecked = checkedCount > 0 && !allChecked
          const isCollapsed = collapsed[moduleName] ?? false

          return (
            <div key={moduleName} className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
              <div
                className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 select-none"
                onClick={() => toggleCollapse(moduleName)}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked }}
                    onChange={() => toggleModule(modulePerms)}
                    onClick={e => e.stopPropagation()}
                    className="w-4 h-4 text-blue-600 rounded cursor-pointer"
                  />
                  <span className="font-semibold text-gray-800 dark:text-white text-sm">{moduleName}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {checkedCount}/{moduleCodes.length}
                  </span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {!isCollapsed && (
                <div className="px-5 pb-4 pt-3 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 gap-x-8 gap-y-3">
                  {modulePerms.map(p => (
                    <label key={p.code} className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checkedCodes.has(p.code)}
                        onChange={() => togglePermission(p.code)}
                        className="w-4 h-4 text-blue-600 rounded mt-0.5 cursor-pointer flex-shrink-0"
                      />
                      <div>
                        <div className="text-sm text-gray-800 dark:text-gray-200 leading-snug">{p.description}</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">{p.code}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
